import { useRef, useEffect, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Brain, Target, Database, AlertTriangle, Check, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { notificationsService } from '@/services/notifications'
import { useQueryResult } from '@/hooks'
import { formatDateTime } from '@/lib/utils'
import { match } from '@/lib/typeUtils'
import type { Notification } from '@/types'

// Discriminated union for notification rendering
type NotificationVariant =
  | { type: 'model'; notification: Notification }
  | { type: 'prediction'; notification: Notification }
  | { type: 'sync'; notification: Notification }
  | { type: 'alert'; notification: Notification }
  | { type: 'system'; notification: Notification }

function toNotificationVariant(notification: Notification): NotificationVariant {
  return { type: notification.type, notification }
}

// Pattern matching for notification icons - exhaustive handling of all types
function getNotificationDisplay(notification: Notification): { icon: ReactNode; color: string } {
  const variant = toNotificationVariant(notification)

  return match(variant, 'type', {
    model: () => ({
      icon: <Brain className="h-5 w-5" />,
      color: 'text-blue-500',
    }),
    prediction: () => ({
      icon: <Target className="h-5 w-5" />,
      color: 'text-purple-500',
    }),
    sync: () => ({
      icon: <Database className="h-5 w-5" />,
      color: 'text-green-500',
    }),
    alert: () => ({
      icon: <AlertTriangle className="h-5 w-5" />,
      color: 'text-red-500',
    }),
    system: () => ({
      icon: <Bell className="h-5 w-5" />,
      color: 'text-gray-500',
    }),
  })
}

interface NotificationsPanelProps {
  isOpen: boolean
  onClose: () => void
}

export function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationsService.list,
  })
  const notificationsResult = useQueryResult(notificationsQuery)
  const notifications = notificationsResult.data?.notifications ?? []

  const markAsReadMutation = useMutation({
    mutationFn: notificationsService.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const markAllAsReadMutation = useMutation({
    mutationFn: notificationsService.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id)
    }
  }

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate()
  }

  if (!isOpen) return null

  const unreadCount = notifications.filter((n) => !n.is_read).length

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div
        ref={panelRef}
        className="relative w-full max-w-md bg-background shadow-2xl h-full flex flex-col animate-in slide-in-from-right duration-300"
      >
        <Card className="flex-1 flex flex-col rounded-none border-0 shadow-none">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle>Notifications</CardTitle>
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="h-5 min-w-5 px-1.5">
                    {unreadCount}
                  </Badge>
                )}
              </div>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  disabled={markAllAsReadMutation.isPending}
                  className="text-xs"
                >
                  <Check className="h-3.5 w-3.5 mr-1" />
                  Mark all read
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0">
            {notificationsResult.isLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <RefreshCw className="h-8 w-8 text-muted-foreground mb-4 animate-spin" />
                <p className="text-sm text-muted-foreground">Loading notifications...</p>
              </div>
            ) : notificationsResult.isError ? (
              <div className="p-4">
                <Alert variant="destructive">
                  <AlertDescription className="flex items-center justify-between">
                    <span>{notificationsResult.errorMessage || 'Failed to load notifications'}</span>
                    {notificationsResult.isRetryable && (
                      <Button variant="outline" size="sm" onClick={notificationsResult.refetch} className="ml-2">
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Retry
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <Bell className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">
                  No notifications yet
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                      !notification.is_read ? 'bg-muted/30' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      {(() => {
                        const { icon, color } = getNotificationDisplay(notification)
                        return (
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full bg-muted ${color}`}
                          >
                            {icon}
                          </div>
                        )
                      })()}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p
                            className={`text-sm ${
                              !notification.is_read
                                ? 'font-semibold'
                                : 'font-medium'
                            }`}
                          >
                            {notification.message}
                          </p>
                          {!notification.is_read && (
                            <div className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(notification.created_at)}
                          </p>
                          <Badge variant="outline" className="text-xs">
                            {notification.type}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

interface NotificationsButtonProps {
  onClick: () => void
}

export function NotificationsButton({ onClick }: NotificationsButtonProps) {
  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationsService.list,
  })
  const notificationsResult = useQueryResult(notificationsQuery)
  const notifications = notificationsResult.data?.notifications ?? []

  const unreadCount = notifications.filter((n) => !n.is_read).length

  return (
    <Button variant="ghost" size="icon" className="relative" onClick={onClick} aria-label="Notifications">
      <Bell className="h-5 w-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Button>
  )
}
